<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import chevronRightIcon from "#ui/assets/icons/chevron_right.svg";
import minusIcon from "#ui/assets/icons/minus.svg";
import plusIcon from "#ui/assets/icons/plus.svg";
import settingsIcon from "#ui/assets/icons/settings.svg";
import { type DecorScreen, nullDecor } from "#ui/decoration/screen";
import DecorIcon from "#ui/primitives/decorIcon/DecorIcon.vue";
import ScrollCenter from "#ui/primitives/scrollCenter/ScrollCenter.vue";
import checkIcon from "#ui/templates/productCard/modifiersPanel/check.svg";
import type { ModifiersPanelRule, ModifiersPanelTab, ModifiersPanelTile } from "./modifiersPanelTypes";

export interface Props {
	open: boolean;
	tabs: ModifiersPanelTab[];
	rule: ModifiersPanelRule | null;
	tiles: ModifiersPanelTile[];
	price: string;
	ready: boolean;
	applyLabel: string;
	pdfMode?: boolean;
	decor?: DecorScreen;
}

withDefaults(defineProps<Props>(), { pdfMode: false, decor: () => nullDecor });

const activeGroupId = defineModel<string | null>("activeGroupId", { default: null });

const emit = defineEmits<{ close: []; apply: []; select: [id: string]; increment: [id: string]; decrement: [id: string] }>();

const tabsRef = ref<HTMLElement | null>(null);
const scrollCenterRef = ref<InstanceType<typeof ScrollCenter> | null>(null);
const tabRefs = new Map<string, HTMLElement>();

const setTabRef = (id: string, el: unknown) => {
	if (el instanceof HTMLElement) tabRefs.set(id, el);
	else tabRefs.delete(id);
};

watch(activeGroupId, async (id) => {
	await nextTick();
	const tab = id ? tabRefs.get(id) : undefined;
	if (tab) scrollCenterRef.value?.scrollToElement(tab);
});
</script>

<template>
	<Transition
		name="cms_panel"
		enter-active-class="cms_panel-enter-active tw:[transition:opacity_0.25s,transform_0.38s_cubic-bezier(0.3,1.1,0.5,1)]"
		leave-active-class="cms_panel-leave-active tw:[transition:opacity_0.25s,transform_0.38s_cubic-bezier(0.3,1.1,0.5,1)]"
		enter-from-class="cms_panel-enter-from tw:opacity-0 tw:[transform:translateY(12%)]"
		leave-to-class="cms_panel-leave-to tw:opacity-0 tw:[transform:translateY(12%)]"
	>
		<div
			v-if="open"
			class="cms_panel tw:[--cms-panel-bg:rgba(30,30,32,0.62)] tw:[--cms-panel-fillet:20px] tw:absolute tw:left-24 tw:right-24 tw:bottom-24 tw:z-5 tw:flex tw:flex-col tw:h-[60%] tw:rounded-[0_40px_48px_48px] tw:[background:var(--cms-panel-bg)] tw:[-webkit-backdrop-filter:blur(30px)_saturate(1.4)] tw:[backdrop-filter:blur(30px)_saturate(1.4)] tw:text-[#ffffff]"
			v-bind="decor.bind('modifiers')"
		>
			<button
				type="button"
				class="cms_panel_notch tw:absolute tw:left-0 tw:bottom-full tw:grid tw:place-items-center tw:w-96 tw:h-44 tw:p-0 tw:[border:0] tw:rounded-[24px_24px_0_0] tw:[background:var(--cms-panel-bg)] tw:[-webkit-backdrop-filter:blur(30px)_saturate(1.4)] tw:[backdrop-filter:blur(30px)_saturate(1.4)] tw:text-inherit tw:after:content-[''] tw:after:absolute tw:after:left-full tw:after:bottom-0 tw:after:w-(--cms-panel-fillet) tw:after:h-(--cms-panel-fillet) tw:after:[background:radial-gradient(circle_at_100%_0,transparent_calc(var(--cms-panel-fillet)_-_0.5px),var(--cms-panel-bg)_var(--cms-panel-fillet))]"
				v-bind="decor.bind('modifiers_notch')"
				@click="emit('close')"
			>
				<DecorIcon :icon="decor.icon('modifiers_notch')" :fallback="settingsIcon" />
			</button>
			<div class="cms_panel_inner tw:flex tw:flex-col tw:flex-[1_1_auto] tw:min-h-0 tw:p-[20px_20px_18px]" v-bind="decor.bind('modifiers_inner')">
				<div
					ref="tabsRef"
					class="cms_panel_tabs tw:flex tw:flex-none tw:gap-8 tw:overflow-x-auto tw:[scrollbar-width:none]"
					v-bind="decor.bind('modifiers_tabs')"
				>
					<ScrollCenter
						ref="scrollCenterRef"
						class="cms_panel_tabs_track tw:flex-none tw:[gap:inherit]"
						axis="x"
						behavior="smooth"
						:container="tabsRef"
						:pdf-mode="pdfMode"
					>
						<button
							v-for="tab in tabs"
							:key="tab.id"
							:ref="(el) => setTabRef(tab.id, el)"
							type="button"
							class="cms_panel_tab tw:flex tw:flex-none tw:items-center tw:gap-8 tw:h-44 tw:p-[0_16px] tw:[border:0] tw:rounded-[24px] tw:[background:rgba(255,255,255,0.08)] tw:text-[#d5d5d5] tw:whitespace-nowrap"
							v-bind="decor.bind('modifiers_tab', { active: tab.id === activeGroupId, required: tab.isRequired && !tab.isDone })"
							@click="activeGroupId = tab.id"
						>
							{{ tab.name }}
							<span
								v-if="tab.isRequired && !tab.isDone"
								class="cms_panel_tab_dot tw:w-10 tw:h-10 tw:rounded-[50%] tw:[background:var(--accentColor,#ff0022)]"
								v-bind="decor.bind('modifiers_tab_dot')"
							/>
							<span
								v-else-if="tab.isRequired"
								class="cms_panel_tab_check tw:grid tw:place-items-center tw:w-20 tw:h-20 tw:rounded-[50%] tw:[background:#2ec26b] tw:text-[#ffffff]"
								v-bind="decor.bind('modifiers_tab_check')"
							>
								<DecorIcon :icon="decor.icon('modifiers_tab_check')" :fallback="checkIcon" />
							</span>
							<span v-else class="cms_panel_tab_count" v-bind="decor.bind('modifiers_tab_count')">{{ tab.count }}</span>
						</button>
					</ScrollCenter>
				</div>
				<p
					v-if="rule"
					class="cms_panel_rule tw:flex tw:flex-none tw:items-center tw:gap-10 tw:m-[14px_4px_10px] tw:text-[#bdbdbd]"
					v-bind="decor.bind('modifiers_rule')"
				>
					<span
						class="cms_panel_rule_badge tw:p-[3px_8px] tw:rounded-[6px] tw:[background:rgba(255,255,255,0.14)] tw:text-[#dddddd] tw:uppercase"
						v-bind="decor.bind('modifiers_rule_badge', { required: rule.isRequired })"
						>{{ rule.badge }}</span
					>
					<span class="cms_panel_rule_text" v-bind="decor.bind('modifiers_rule_text')">{{ rule.text }}</span>
				</p>
				<div
					class="cms_panel_body tw:flex-[1_1_auto] tw:min-h-0 tw:overflow-x-hidden tw:overflow-y-auto tw:[scrollbar-width:none] tw:overscroll-contain"
					v-bind="decor.bind('modifiers_body')"
				>
					<div class="cms_panel_grid tw:grid tw:grid-cols-[repeat(3,minmax(0,1fr))] tw:gap-14" v-bind="decor.bind('modifiers_grid')">
						<button
							v-for="tile in tiles"
							:key="tile.id"
							type="button"
							class="cms_panel_item tw:flex tw:flex-col tw:items-center tw:min-w-0 tw:p-[14px_10px_12px] tw:[border:3px_solid_#ffffff] tw:rounded-[26px] tw:[background:rgba(255,255,255,0.06)] tw:text-inherit tw:[transition:background_0.15s,opacity_0.15s]"
							v-bind="decor.bind('modifiers_item', { selected: tile.amount > 0, dimmed: tile.isDimmed })"
							@click="emit('select', tile.id)"
						>
							<img
								:src="tile.image"
								alt=""
								class="cms_panel_item_image tw:w-60 tw:h-60 tw:object-contain"
								decoding="async"
								v-bind="decor.bind('modifiers_item_image')"
							>
							<span class="cms_panel_item_name tw:mt-6 tw:text-center" v-bind="decor.bind('modifiers_item_name')">{{ tile.name }}</span>
							<span class="cms_panel_item_price" v-bind="decor.bind('modifiers_item_price')">{{ tile.price }}</span>
							<span
								v-if="tile.isCounter"
								class="cms_panel_item_stepper tw:flex tw:items-center tw:justify-between tw:w-[88%] tw:mt-auto tw:[border:2px_solid_#ffffff] tw:rounded-[20px]"
								v-bind="decor.bind('modifiers_item_stepper')"
								@click.stop
							>
								<button
									type="button"
									class="cms_panel_item_step tw:grid tw:place-items-center tw:w-28 tw:h-28 tw:p-0 tw:[border:0] tw:rounded-[50%] tw:[background:var(--accentColor,#ff0022)] tw:text-[#ffffff] tw:disabled:opacity-35"
									:disabled="tile.amount === 0"
									v-bind="decor.bind('modifiers_item_step')"
									@click.stop="emit('decrement', tile.id)"
								>
									<DecorIcon :icon="decor.icon('modifiers_item_minus')" :fallback="minusIcon" />
								</button>
								<b class="cms_panel_item_count" v-bind="decor.bind('modifiers_item_count')">{{ tile.amount }}</b>
								<button
									type="button"
									class="cms_panel_item_step tw:grid tw:place-items-center tw:w-28 tw:h-28 tw:p-0 tw:[border:0] tw:rounded-[50%] tw:[background:var(--accentColor,#ff0022)] tw:text-[#ffffff] tw:disabled:opacity-35"
									:disabled="!tile.canIncrement"
									v-bind="decor.bind('modifiers_item_step')"
									@click.stop="emit('increment', tile.id)"
								>
									<DecorIcon :icon="decor.icon('modifiers_item_plus')" :fallback="plusIcon" />
								</button>
							</span>
							<span
								v-else
								class="cms_panel_item_radio tw:grid tw:place-items-center tw:w-28 tw:h-28 tw:mt-auto tw:[border:2px_solid_#ffffff] tw:rounded-[50%] tw:text-transparent"
								v-bind="decor.bind('modifiers_item_radio', { selected: tile.amount > 0 })"
							>
								<DecorIcon :icon="decor.icon('modifiers_item_radio')" :fallback="checkIcon" />
							</span>
						</button>
					</div>
				</div>
				<div class="cms_panel_footer tw:flex tw:flex-none tw:items-center tw:justify-end tw:gap-16 tw:mt-14" v-bind="decor.bind('modifiers_footer')">
					<span class="cms_panel_price" v-bind="decor.bind('modifiers_price')">{{ price }}</span>
					<button
						type="button"
						class="cms_panel_apply tw:grid tw:place-items-center tw:w-64 tw:h-64 tw:p-0 tw:[border:0] tw:rounded-[50%] tw:[background:#bdbdbc] tw:text-[#ffffff]"
						:aria-label="applyLabel"
						v-bind="decor.bind('modifiers_apply', { ready })"
						@click="emit('apply')"
					>
						<DecorIcon :icon="decor.icon('modifiers_apply')" :fallback="ready ? plusIcon : chevronRightIcon" />
					</button>
				</div>
			</div>
		</div>
	</Transition>
</template>
